
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "@/store/auth";
import { useUiStore } from "@/store/ui";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/features/states";
import { currencySymbol } from "@/lib/format";
import { toast } from "sonner";
import {
  ArrowLeft,
  MapPin,
  Package,
  Banknote,
  Bike,
  Car,
  CarTaxiFront,
  Truck,
  Plus,
  Trash2,
  Utensils,
  ShoppingCart,
  Pill,
  FileText,
  ListChecks,
  Droplets,
  Camera,
  Clock,
  Zap,
  Calendar,
  TrendingUp,
  Receipt,
  X,
} from "lucide-react";
import type { QuoteBreakdown } from "@/lib/types";

const CATEGORIES = [
  "parcel",
  "food",
  "grocery",
  "pharmacy",
  "courier",
  "errand",
  "water",
] as const;
type Category = (typeof CATEGORIES)[number];

const CATEGORY_ICONS: Record<Category, React.ElementType> = {
  parcel: Package,
  food: Utensils,
  grocery: ShoppingCart,
  pharmacy: Pill,
  courier: FileText,
  errand: ListChecks,
  water: Droplets,
};

interface FoodItem {
  name: string;
  qty: string;
  price: string;
}

interface GroceryItem {
  name: string;
  qty: string;
  estimatedPrice: string;
}

interface MedicineItem {
  name: string;
  qty: string;
}

export function JobCreateView() {
  const { t } = useTranslation();
  const navigate = useUiStore((s) => s.navigate);
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(false);
  // Currency symbol derived from the user's country code (Djibouti → Fdj,
  // Somalia → Sh, Ethiopia → ETB). Passed to translated labels so we never
  // hardcode a currency in a locale string.
  const currency = currencySymbol(user?.countryCode);

  // Read preselected category from sessionStorage (set by dashboard quick-tiles).
  const [form, setForm] = useState(() => {
    let initialCategory: Category = "parcel";
    if (typeof window !== "undefined") {
      const stored = window.sessionStorage.getItem("jobCreateCategory");
      if (stored && (CATEGORIES as readonly string[]).includes(stored)) {
        initialCategory = stored as Category;
        window.sessionStorage.removeItem("jobCreateCategory");
      }
    }
    return {
      parcelDescription: "",
      parcelCategory: initialCategory as string,
      parcelWeightKg: "",
      parcelSize: "small",
      fragile: false,
      pickupAddress: "",
      pickupNotes: "",
      dropoffAddress: "",
      dropoffNotes: "",
      vehicleRequired: "any",
      contactPhone: user?.phone || "",
      customerOffer: "",
    };
  });

  // Food metadata
  const [food, setFood] = useState({
    restaurantName: "",
    orderTotal: "",
    specialInstructions: "",
  });
  const [foodItems, setFoodItems] = useState<FoodItem[]>([{ name: "", qty: "1", price: "" }]);

  // Grocery metadata
  const [grocery, setGrocery] = useState({
    storeName: "",
    estimatedTotal: "",
    substitutionsAllowed: true,
    shoppingWindow: "ASAP",
  });
  const [groceryItems, setGroceryItems] = useState<GroceryItem[]>([{ name: "", qty: "1", estimatedPrice: "" }]);

  // Pharmacy metadata (Phase 3)
  const [pharmacy, setPharmacy] = useState({
    pharmacyName: "",
    prescriptionRequired: false,
    prescriptionNumber: "",
    temperatureControlled: false,
  });
  const [medicineItems, setMedicineItems] = useState<MedicineItem[]>([{ name: "", qty: "1" }]);

  // Courier metadata (Phase 3)
  const [courier, setCourier] = useState({
    documentType: "contract",
    signatureRequired: true,
    urgency: "normal",
  });

  // Errand metadata (Phase 3)
  const [errand, setErrand] = useState({
    errandType: "bill",
    errandLocation: "",
    errandNotes: "",
  });

  // V1.0 Pickup & Deliver enhancements:
  // 1. Proof upload — photo of the shopping receipt / order confirmation
  //    (core differentiating feature per the business spec).
  // 2. Schedule delivery — immediate (on-demand) vs scheduled-for-later.
  // 3. Instant quote — real-time delivery-fee calculation.
  const [receiptPhotoUrl, setReceiptPhotoUrl] = useState("");
  const [scheduleMode, setScheduleMode] = useState<"immediate" | "scheduled">("immediate");
  const [scheduledFor, setScheduledFor] = useState("");
  const [quote, setQuote] = useState<QuoteBreakdown | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);

  const set = (k: string, v: any) => setForm((f) => ({ ...f, [k]: v }));

  // V1.0: compute instant quote whenever the inputs that affect the fee change.
  // We debounce via a simple effect — the quote endpoint is cheap (pure calc).
  const computeInstantQuote = async () => {
    // Only compute if we have at least a dropoff address (pickup optional).
    if (!form.dropoffAddress.trim()) {
      setQuote(null);
      return;
    }
    setQuoteLoading(true);
    const res = await api.jobs.quote({
      weightKg: form.parcelWeightKg ? parseFloat(form.parcelWeightKg) : undefined,
      size: (form.parcelSize as "small" | "medium" | "large") || undefined,
      scheduledFor: scheduleMode === "scheduled" && scheduledFor ? scheduledFor : null,
      mode: "pickup_deliver",
    });
    setQuoteLoading(false);
    if (res.ok && res.data) {
      setQuote(res.data.quote);
      // Auto-fill the customer offer with the quoted total so the customer
      // sees the recommended price (they can still override it).
      if (!form.customerOffer) {
        set("customerOffer", String(res.data.quote.total.toFixed(2)));
      }
    }
  };

  // Recompute quote when relevant inputs change.
  useEffect(() => {
    const t = setTimeout(computeInstantQuote, 400);
    return () => clearTimeout(t);
  }, [form.parcelWeightKg, form.parcelSize, scheduleMode, scheduledFor, form.dropoffAddress]);

  // V1.0: handle receipt photo upload. In dev/demo we accept a URL or a
  // base64 data URL (from a file input). Production would upload to object
  // storage and store the returned URL.
  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image too large (max 5MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setReceiptPhotoUrl(reader.result as string);
      toast.success(t("jobCreate.photoUploaded"));
    };
    reader.readAsDataURL(file);
  };

  const updateFoodItem = (i: number, patch: Partial<FoodItem>) =>
    setFoodItems((items) => items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const addFoodItem = () => setFoodItems((items) => [...items, { name: "", qty: "1", price: "" }]);
  const removeFoodItem = (i: number) => setFoodItems((items) => items.filter((_, idx) => idx !== i));

  const updateGroceryItem = (i: number, patch: Partial<GroceryItem>) =>
    setGroceryItems((items) => items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const addGroceryItem = () => setGroceryItems((items) => [...items, { name: "", qty: "1", estimatedPrice: "" }]);
  const removeGroceryItem = (i: number) => setGroceryItems((items) => items.filter((_, idx) => idx !== i));

  const updateMedicineItem = (i: number, patch: Partial<MedicineItem>) =>
    setMedicineItems((items) => items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const addMedicineItem = () => setMedicineItems((items) => [...items, { name: "", qty: "1" }]);
  const removeMedicineItem = (i: number) => setMedicineItems((items) => items.filter((_, idx) => idx !== i));

  const buildMetadata = () => {
    const cat = form.parcelCategory;
    if (cat === "food") {
      return {
        restaurantName: food.restaurantName.trim() || undefined,
        orderItems: foodItems
          .filter((it) => it.name.trim())
          .map((it) => ({ name: it.name.trim(), qty: parseInt(it.qty || "1", 10) || 1, price: parseFloat(it.price || "0") || 0 })),
        orderTotal: food.orderTotal ? parseFloat(food.orderTotal) : undefined,
        specialInstructions: food.specialInstructions.trim() || undefined,
      };
    }
    if (cat === "grocery") {
      return {
        storeName: grocery.storeName.trim() || undefined,
        items: groceryItems
          .filter((it) => it.name.trim())
          .map((it) => ({ name: it.name.trim(), qty: parseInt(it.qty || "1", 10) || 1, estimatedPrice: parseFloat(it.estimatedPrice || "0") || 0 })),
        estimatedTotal: grocery.estimatedTotal ? parseFloat(grocery.estimatedTotal) : undefined,
        substitutionsAllowed: grocery.substitutionsAllowed,
        shoppingWindow: grocery.shoppingWindow.trim() || undefined,
      };
    }
    if (cat === "pharmacy") {
      return {
        pharmacyName: pharmacy.pharmacyName.trim() || undefined,
        prescriptionRequired: pharmacy.prescriptionRequired,
        prescriptionNumber: pharmacy.prescriptionNumber.trim() || undefined,
        medicineItems: medicineItems
          .filter((it) => it.name.trim())
          .map((it) => ({ name: it.name.trim(), qty: parseInt(it.qty || "1", 10) || 1 })),
        temperatureControlled: pharmacy.temperatureControlled,
      };
    }
    if (cat === "courier") {
      return {
        documentType: courier.documentType,
        signatureRequired: courier.signatureRequired,
        urgency: courier.urgency,
      };
    }
    if (cat === "errand") {
      return {
        errandType: errand.errandType,
        errandLocation: errand.errandLocation.trim() || undefined,
        errandNotes: errand.errandNotes.trim() || undefined,
      };
    }
    return undefined;
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.parcelDescription.trim()) return toast.error(t("jobCreate.errDescribe"));
    if (!form.pickupAddress.trim()) return toast.error(t("jobCreate.errPickup"));
    if (!form.dropoffAddress.trim()) return toast.error(t("jobCreate.errDropoff"));
    const offer = parseFloat(form.customerOffer);
    if (!offer || offer <= 0) return toast.error(t("jobCreate.errOffer", { currency }));
    setLoading(true);
    const metadata = buildMetadata();
    const res = await api.jobs.create({
      parcelDescription: form.parcelDescription.trim(),
      parcelCategory: form.parcelCategory,
      parcelWeightKg: form.parcelWeightKg ? parseFloat(form.parcelWeightKg) : undefined,
      parcelSize: form.parcelSize,
      fragile: form.fragile,
      pickupAddress: form.pickupAddress.trim(),
      pickupNotes: form.pickupNotes.trim() || undefined,
      dropoffAddress: form.dropoffAddress.trim(),
      dropoffNotes: form.dropoffNotes.trim() || undefined,
      vehicleRequired: form.vehicleRequired,
      contactPhone: form.contactPhone.trim() || undefined,
      customerOffer: offer,
      // V1.0: dual-mode (pickup_deliver), proof upload, schedule, quote breakdown.
      mode: "pickup_deliver",
      receiptPhotoUrl: receiptPhotoUrl || undefined,
      scheduledFor:
        scheduleMode === "scheduled" && scheduledFor
          ? new Date(scheduledFor).toISOString()
          : undefined,
      quotedDistanceKm: quote?.distanceKm,
      quotedDeliveryFee: quote?.total,
      quotedServiceFee: quote?.serviceFee,
      ...(metadata ? { metadata } : {}),
    });
    setLoading(false);
    if (!res.ok) return toast.error(res.error || t("errors.generic"));
    toast.success(t("jobCreate.successPosted"));
    navigate(`app/jobs/${res.data!.job.id}`);
  };

  const categoryLabel = (c: string) =>
    c === "parcel" ? t("jobCreate.categoryParcel")
      : c === "food" ? t("jobCreate.categoryFood")
        : c === "grocery" ? t("jobCreate.categoryGrocery")
          : c === "pharmacy" ? t("jobCreate.categoryPharmacy")
            : c === "courier" ? t("jobCreate.categoryCourier")
              : c === "errand" ? t("jobCreate.categoryErrand")
                : c === "water" ? t("jobCreate.categoryWater")
                  : t("jobCreate.categoryParcel");

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => navigate("app/jobs")}>
          <ArrowLeft className="h-4 w-4 rtl-flip" />
        </Button>
        <PageHeader title={t("jobCreate.title")} subtitle={t("jobCreate.subtitle")} />
      </div>

      <form onSubmit={submit} className="space-y-4">
        {/* Parcel */}
        <Card>
          <CardContent className="space-y-3 p-4">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Package className="h-4 w-4 text-primary" /> {t("jobCreate.parcelDetails")}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="desc">{t("jobCreate.whatSending")} *</Label>
              <Textarea
                id="desc"
                placeholder={t("jobCreate.whatSendingPlaceholder")}
                value={form.parcelDescription}
                onChange={(e) => set("parcelDescription", e.target.value)}
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t("jobCreate.category")}</Label>
                <select
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={form.parcelCategory}
                  onChange={(e) => set("parcelCategory", e.target.value)}
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>{categoryLabel(c)}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="weight">{t("jobCreate.weight")}</Label>
                <Input id="weight" type="number" step="0.1" placeholder="0.5" value={form.parcelWeightKg} onChange={(e) => set("parcelWeightKg", e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t("jobCreate.size")}</Label>
                <RadioGroup value={form.parcelSize} onValueChange={(v) => set("parcelSize", v)} className="flex gap-3 pt-1">
                  {["small", "medium", "large"].map((s) => (
                    <div key={s} className="flex items-center gap-1.5">
                      <RadioGroupItem id={`size-${s}`} value={s} />
                      <Label htmlFor={`size-${s}`} className="cursor-pointer text-sm">
                        {s === "small" ? t("jobCreate.sizeSmall") : s === "medium" ? t("jobCreate.sizeMedium") : t("jobCreate.sizeLarge")}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={form.fragile} onCheckedChange={(v) => set("fragile", v === true)} />
                  {t("jobCreate.fragile")}
                </label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Food metadata */}
        {form.parcelCategory === "food" && (
          <Card className="border-amber-300/60">
            <CardContent className="space-y-3 p-4">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Utensils className="h-4 w-4 text-amber-600" /> {t("jobCreate.restaurantDetails")}
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="restaurant">{t("jobCreate.restaurantName")}</Label>
                  <Input id="restaurant" placeholder={t("jobCreate.restaurantNamePlaceholder")} value={food.restaurantName} onChange={(e) => setFood({ ...food, restaurantName: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="orderTotal">{t("jobCreate.orderTotal", { currency })}</Label>
                  <Input id="orderTotal" type="number" placeholder="450" value={food.orderTotal} onChange={(e) => setFood({ ...food, orderTotal: e.target.value })} />
                </div>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label>{t("jobCreate.orderItems")}</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addFoodItem}>
                    <Plus className="mr-1 h-3.5 w-3.5" /> {t("jobCreate.addItem")}
                  </Button>
                </div>
                <div className="space-y-2">
                  {foodItems.map((it, i) => (
                    <div key={i} className="flex gap-2">
                      <Input className="flex-1" placeholder={t("jobCreate.itemNamePlaceholder")} value={it.name} onChange={(e) => updateFoodItem(i, { name: e.target.value })} />
                      <Input className="w-16" type="number" min="1" placeholder={t("jobCreate.qty")} value={it.qty} onChange={(e) => updateFoodItem(i, { qty: e.target.value })} aria-label={t("jobCreate.qty")} />
                      <Input className="w-24" type="number" placeholder={t("common.price")} value={it.price} onChange={(e) => updateFoodItem(i, { price: e.target.value })} aria-label={t("common.price")} />
                      <Button type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0 text-destructive" onClick={() => removeFoodItem(i)} disabled={foodItems.length === 1} aria-label={t("common.delete")}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="special">{t("jobCreate.specialInstructions")}</Label>
                <Textarea id="special" rows={2} placeholder={t("jobCreate.specialInstructionsPlaceholder")} value={food.specialInstructions} onChange={(e) => setFood({ ...food, specialInstructions: e.target.value })} />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Grocery metadata */}
        {form.parcelCategory === "grocery" && (
          <Card className="border-amber-300/60">
            <CardContent className="space-y-3 p-4">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <ShoppingCart className="h-4 w-4 text-amber-600" /> {t("jobCreate.storeDetails")}
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="store">{t("jobCreate.storeName")}</Label>
                  <Input id="store" placeholder={t("jobCreate.storeNamePlaceholder")} value={grocery.storeName} onChange={(e) => setGrocery({ ...grocery, storeName: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="estTotal">{t("jobCreate.estimatedTotal", { currency })}</Label>
                  <Input id="estTotal" type="number" placeholder="800" value={grocery.estimatedTotal} onChange={(e) => setGrocery({ ...grocery, estimatedTotal: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="window">{t("jobCreate.shoppingWindow")}</Label>
                  <Input id="window" placeholder={t("jobCreate.shoppingWindowPlaceholder")} value={grocery.shoppingWindow} onChange={(e) => setGrocery({ ...grocery, shoppingWindow: e.target.value })} />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <Switch id="subs" checked={grocery.substitutionsAllowed} onCheckedChange={(v) => setGrocery({ ...grocery, substitutionsAllowed: v })} />
                  <Label htmlFor="subs" className="cursor-pointer text-sm">{t("jobCreate.allowSubstitutions")}</Label>
                </div>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label>{t("jobCreate.shoppingItems")}</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addGroceryItem}>
                    <Plus className="mr-1 h-3.5 w-3.5" /> {t("jobCreate.addItem")}
                  </Button>
                </div>
                <div className="space-y-2">
                  {groceryItems.map((it, i) => (
                    <div key={i} className="flex gap-2">
                      <Input className="flex-1" placeholder={t("jobCreate.itemPlaceholder")} value={it.name} onChange={(e) => updateGroceryItem(i, { name: e.target.value })} />
                      <Input className="w-16" type="number" min="1" placeholder={t("jobCreate.qty")} value={it.qty} onChange={(e) => updateGroceryItem(i, { qty: e.target.value })} aria-label={t("jobCreate.qty")} />
                      <Input className="w-28" type="number" placeholder={t("jobCreate.estPrice")} value={it.estimatedPrice} onChange={(e) => updateGroceryItem(i, { estimatedPrice: e.target.value })} aria-label={t("jobCreate.estPrice")} />
                      <Button type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0 text-destructive" onClick={() => removeGroceryItem(i)} disabled={groceryItems.length === 1} aria-label={t("common.delete")}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pharmacy metadata (Phase 3) */}
        {form.parcelCategory === "pharmacy" && (
          <Card className="border-rose-300/60">
            <CardContent className="space-y-3 p-4">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Pill className="h-4 w-4 text-rose-600" /> {t("jobCreate.pharmacyDetails")}
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="pharmName">{t("jobCreate.pharmacyName")}</Label>
                  <Input id="pharmName" placeholder={t("jobCreate.pharmacyNamePlaceholder")} value={pharmacy.pharmacyName} onChange={(e) => setPharmacy({ ...pharmacy, pharmacyName: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="rxNum">{t("jobCreate.prescriptionNumber")}</Label>
                  <Input id="rxNum" placeholder={t("jobCreate.prescriptionNumberPlaceholder")} value={pharmacy.prescriptionNumber} onChange={(e) => setPharmacy({ ...pharmacy, prescriptionNumber: e.target.value })} />
                </div>
              </div>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={pharmacy.prescriptionRequired} onCheckedChange={(v) => setPharmacy({ ...pharmacy, prescriptionRequired: v === true })} />
                  {t("jobCreate.prescriptionRequired")}
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={pharmacy.temperatureControlled} onCheckedChange={(v) => setPharmacy({ ...pharmacy, temperatureControlled: v === true })} />
                  {t("jobCreate.temperatureControlled")}
                </label>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label>{t("jobCreate.medicineItems")}</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addMedicineItem}>
                    <Plus className="mr-1 h-3.5 w-3.5" /> {t("jobCreate.addItem")}
                  </Button>
                </div>
                <div className="space-y-2">
                  {medicineItems.map((it, i) => (
                    <div key={i} className="flex gap-2">
                      <Input className="flex-1" placeholder={t("jobCreate.itemNamePlaceholder")} value={it.name} onChange={(e) => updateMedicineItem(i, { name: e.target.value })} />
                      <Input className="w-20" type="number" min="1" placeholder={t("jobCreate.qty")} value={it.qty} onChange={(e) => updateMedicineItem(i, { qty: e.target.value })} aria-label={t("jobCreate.qty")} />
                      <Button type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0 text-destructive" onClick={() => removeMedicineItem(i)} disabled={medicineItems.length === 1} aria-label={t("common.delete")}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Courier metadata (Phase 3) */}
        {form.parcelCategory === "courier" && (
          <Card className="border-sky-300/60">
            <CardContent className="space-y-3 p-4">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <FileText className="h-4 w-4 text-sky-600" /> {t("jobCreate.courierDetails")}
              </div>
              <div className="space-y-1.5">
                <Label>{t("jobCreate.documentType")}</Label>
                <RadioGroup value={courier.documentType} onValueChange={(v) => setCourier({ ...courier, documentType: v })} className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {[
                    { v: "contract", l: t("jobCreate.documentTypeContract") },
                    { v: "legal", l: t("jobCreate.documentTypeLegal") },
                    { v: "bank", l: t("jobCreate.documentTypeBank") },
                    { v: "other", l: t("jobCreate.documentTypeOther") },
                  ].map((o) => (
                    <label key={o.v} className={`flex cursor-pointer items-center justify-center rounded-lg border p-2 text-xs transition ${courier.documentType === o.v ? "border-primary bg-primary/5" : "border-border"}`}>
                      <RadioGroupItem value={o.v} className="sr-only" />
                      {o.l}
                    </label>
                  ))}
                </RadioGroup>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>{t("jobCreate.urgency")}</Label>
                  <RadioGroup value={courier.urgency} onValueChange={(v) => setCourier({ ...courier, urgency: v })} className="grid grid-cols-3 gap-2">
                    {[
                      { v: "normal", l: t("jobCreate.urgencyNormal") },
                      { v: "urgent", l: t("jobCreate.urgencyUrgent") },
                      { v: "same_day", l: t("jobCreate.urgencySameDay") },
                    ].map((o) => (
                      <label key={o.v} className={`flex cursor-pointer items-center justify-center rounded-lg border p-2 text-xs transition ${courier.urgency === o.v ? "border-primary bg-primary/5" : "border-border"}`}>
                        <RadioGroupItem value={o.v} className="sr-only" />
                        {o.l}
                      </label>
                    ))}
                  </RadioGroup>
                </div>
                <label className="flex items-center gap-2 pt-6 text-sm">
                  <Switch checked={courier.signatureRequired} onCheckedChange={(v) => setCourier({ ...courier, signatureRequired: v })} />
                  {t("jobCreate.signatureRequired")}
                </label>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Errand metadata (Phase 3) */}
        {form.parcelCategory === "errand" && (
          <Card className="border-violet-300/60">
            <CardContent className="space-y-3 p-4">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <ListChecks className="h-4 w-4 text-violet-600" /> {t("jobCreate.errandDetails")}
              </div>
              <div className="space-y-1.5">
                <Label>{t("jobCreate.errandType")}</Label>
                <RadioGroup value={errand.errandType} onValueChange={(v) => setErrand({ ...errand, errandType: v })} className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {[
                    { v: "bill", l: t("jobCreate.errandTypeBill") },
                    { v: "pickup", l: t("jobCreate.errandTypePickup") },
                    { v: "queue", l: t("jobCreate.errandTypeQueue") },
                    { v: "other", l: t("jobCreate.errandTypeOther") },
                  ].map((o) => (
                    <label key={o.v} className={`flex cursor-pointer items-center justify-center rounded-lg border p-2 text-xs transition ${errand.errandType === o.v ? "border-primary bg-primary/5" : "border-border"}`}>
                      <RadioGroupItem value={o.v} className="sr-only" />
                      {o.l}
                    </label>
                  ))}
                </RadioGroup>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="errLoc">{t("jobCreate.errandLocation")}</Label>
                <Input id="errLoc" placeholder={t("jobCreate.pickupPlaceholder")} value={errand.errandLocation} onChange={(e) => setErrand({ ...errand, errandLocation: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="errNotes">{t("jobCreate.errandNotes")}</Label>
                <Textarea id="errNotes" rows={2} placeholder={t("jobCreate.specialInstructionsPlaceholder")} value={errand.errandNotes} onChange={(e) => setErrand({ ...errand, errandNotes: e.target.value })} />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Route */}
        <Card>
          <CardContent className="space-y-3 p-4">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <MapPin className="h-4 w-4 text-emerald-600" /> {t("jobCreate.pickupDropoff")}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pickup">{t("jobCreate.pickupAddress")} *</Label>
              <Input id="pickup" placeholder={t("jobCreate.pickupPlaceholder")} value={form.pickupAddress} onChange={(e) => set("pickupAddress", e.target.value)} />
              <Input placeholder={t("jobCreate.pickupNotes")} value={form.pickupNotes} onChange={(e) => set("pickupNotes", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="dropoff">{t("jobCreate.dropoffAddress")} *</Label>
              <Input id="dropoff" placeholder={t("jobCreate.dropoffPlaceholder")} value={form.dropoffAddress} onChange={(e) => set("dropoffAddress", e.target.value)} />
              <Input placeholder={t("jobCreate.dropoffNotes")} value={form.dropoffNotes} onChange={(e) => set("dropoffNotes", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="phone">{t("jobCreate.contactPhone")}</Label>
              <Input id="phone" placeholder="+253 9..." value={form.contactPhone} onChange={(e) => set("contactPhone", e.target.value)} />
            </div>
          </CardContent>
        </Card>

        {/* Vehicle + price */}
        <Card>
          <CardContent className="space-y-3 p-4">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Bike className="h-4 w-4 text-primary" /> {t("jobCreate.vehicleOffer")}
            </div>
            <div className="space-y-1.5">
              <Label>{t("jobCreate.requiredVehicle")}</Label>
              <RadioGroup value={form.vehicleRequired} onValueChange={(v) => set("vehicleRequired", v)} className="grid grid-cols-3 gap-2">
                {[
                  { v: "any", l: t("jobCreate.vehicleAny"), icon: Package },
                  { v: "motorcycle", l: t("jobCreate.vehicleMoto"), icon: Bike },
                  { v: "car", l: t("jobCreate.vehicleCar"), icon: Car },
                  { v: "badgag", l: t("jobCreate.vehicleBadgag"), icon: CarTaxiFront },
                  { v: "pickup", l: t("jobCreate.vehiclePickup"), icon: Truck },
                  { v: "truck", l: t("jobCreate.vehicleTruck"), icon: Truck },
                ].map((o) => (
                  <label key={o.v} className={`flex cursor-pointer flex-col items-center gap-1 rounded-lg border p-2 text-xs transition ${form.vehicleRequired === o.v ? "border-primary bg-primary/5" : "border-border"}`}>
                    <RadioGroupItem value={o.v} className="sr-only" />
                    <o.icon className="h-5 w-5" />
                    {o.l}
                  </label>
                ))}
              </RadioGroup>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="offer">{t("jobCreate.yourOffer", { currency })} *</Label>
              <div className="relative">
                <Banknote className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground rtl-flip" style={{ left: "auto", right: "0.75rem" }} />
                <Input id="offer" type="number" className="pl-9" placeholder="150" value={form.customerOffer} onChange={(e) => set("customerOffer", e.target.value)} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* V1.0 — Proof of order (receipt photo). Core differentiating feature
            for Pickup & Deliver mode: the customer uploads a photo of their
            shopping receipt / order confirmation so the courier knows what to
            pick up. */}
        <Card>
          <CardContent className="space-y-3 p-4">
            <div className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-primary" />
              <h3 className="font-semibold">{t("jobCreate.proofUpload")}</h3>
              <Badge variant="secondary" className="ml-auto text-[10px]">V1.0</Badge>
            </div>
            <p className="text-xs text-muted-foreground">{t("jobCreate.proofUploadDesc")}</p>
            {receiptPhotoUrl ? (
              <div className="relative overflow-hidden rounded-lg border">
                <img src={receiptPhotoUrl} alt="Receipt" className="max-h-48 w-full object-cover" />
                <button
                  type="button"
                  onClick={() => setReceiptPhotoUrl("")}
                  className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-black/60 text-white"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <label className="flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed border-border p-6 text-center transition hover:border-primary/40 hover:bg-primary/5">
                <Camera className="h-8 w-8 text-muted-foreground" />
                <span className="text-sm font-medium">{t("jobCreate.uploadPhoto")}</span>
                <span className="text-[10px] text-muted-foreground">JPG, PNG · max 5MB</span>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="sr-only"
                  onChange={handlePhotoUpload}
                />
              </label>
            )}
          </CardContent>
        </Card>

        {/* V1.0 — Schedule delivery: immediate (on-demand) vs scheduled. */}
        <Card>
          <CardContent className="space-y-3 p-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              <h3 className="font-semibold">{t("jobCreate.scheduleDelivery")}</h3>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setScheduleMode("immediate")}
                className={`flex flex-col items-center gap-1 rounded-lg border p-3 text-center transition ${
                  scheduleMode === "immediate"
                    ? "border-primary bg-primary/5"
                    : "border-border hover:bg-muted/50"
                }`}
              >
                <Zap className="h-5 w-5 text-amber-500" />
                <span className="text-xs font-semibold">{t("jobCreate.immediate")}</span>
                <span className="text-[10px] text-muted-foreground">{t("jobCreate.onDemand")}</span>
              </button>
              <button
                type="button"
                onClick={() => setScheduleMode("scheduled")}
                className={`flex flex-col items-center gap-1 rounded-lg border p-3 text-center transition ${
                  scheduleMode === "scheduled"
                    ? "border-primary bg-primary/5"
                    : "border-border hover:bg-muted/50"
                }`}
              >
                <Clock className="h-5 w-5 text-primary" />
                <span className="text-xs font-semibold">{t("jobCreate.scheduledFor")}</span>
                <span className="text-[10px] text-muted-foreground">{t("jobCreate.pickATime")}</span>
              </button>
            </div>
            {scheduleMode === "scheduled" && (
              <div className="space-y-1.5">
                <Label htmlFor="scheduledFor">{t("jobCreate.pickupDate")}</Label>
                <Input
                  id="scheduledFor"
                  type="datetime-local"
                  value={scheduledFor}
                  onChange={(e) => setScheduledFor(e.target.value)}
                />
              </div>
            )}
            {scheduleMode === "immediate" && (
              <p className="flex items-center gap-1 text-xs text-amber-600">
                <Zap className="h-3 w-3" /> {t("jobCreate.instantPremiumNote")}
              </p>
            )}
          </CardContent>
        </Card>

        {/* V1.0 — Instant quote breakdown. Shows the real-time fee calculation
            based on distance, weight, size, schedule, and peak surge. */}
        {quote && (
          <Card className="border-primary/30 bg-primary/5">
            <CardContent className="space-y-2 p-4">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-primary" />
                <h3 className="font-semibold">{t("jobCreate.instantQuote")}</h3>
                {quoteLoading && (
                  <span className="ml-auto text-xs text-muted-foreground">{t("jobCreate.updating")}</span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{t("jobCreate.quoteDesc")}</p>
              <div className="space-y-1.5 rounded-lg bg-background/60 p-3 text-sm">
                <QuoteRow label={t("quote.distance")} value={`${quote.distanceKm} ${t("quote.km")}`} />
                {quote.weightFee > 0 && (
                  <QuoteRow label={t("quote.weightFee")} value={`${currency} ${quote.weightFee.toFixed(2)}`} />
                )}
                {quote.sizeFee > 0 && (
                  <QuoteRow label={t("quote.sizeFee")} value={`${currency} ${quote.sizeFee.toFixed(2)}`} />
                )}
                {quote.instantPremium > 0 && (
                  <QuoteRow
                    label={t("quote.instantPremium")}
                    value={`+${currency} ${quote.instantPremium.toFixed(2)}`}
                    accent="text-amber-600"
                  />
                )}
                {quote.peakSurge > 0 && (
                  <QuoteRow
                    label={t("quote.peakSurge")}
                    value={`+${currency} ${quote.peakSurge.toFixed(2)}`}
                    accent="text-orange-600"
                  />
                )}
                {quote.isPeak && (
                  <p className="flex items-center gap-1 text-[10px] font-medium text-orange-600">
                    <Zap className="h-3 w-3" /> {t("quote.peakNow")}
                  </p>
                )}
                <div className="border-t pt-1.5">
                  <QuoteRow
                    label={t("quote.serviceFee")}
                    value={`${currency} ${quote.serviceFee.toFixed(2)}`}
                    muted
                  />
                  <div className="mt-1 flex items-center justify-between">
                    <span className="font-bold">{t("quote.total")}</span>
                    <span className="text-lg font-bold text-primary">
                      {currency} {quote.total.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="flex gap-2">
          <Button type="button" variant="outline" className="flex-1" onClick={() => navigate("app/jobs")}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" className="flex-1" disabled={loading}>
            {loading ? t("jobCreate.posting") : t("jobCreate.postJob")}
          </Button>
        </div>
      </form>
    </div>
  );
}

/** Quote breakdown row helper. */
function QuoteRow({
  label,
  value,
  muted,
  accent,
}: {
  label: string;
  value: string;
  muted?: boolean;
  accent?: string;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className={muted ? "text-xs text-muted-foreground" : "text-xs"}>{label}</span>
      <span className={`text-xs font-medium ${accent || ""}`}>{value}</span>
    </div>
  );
}
